import React, { useEffect, useRef, useState } from 'react'
import { downloadFileWithAuth, getNextProcessStatus, printDocument, translate } from '../../../lib'
import { useCore } from '../../../hooks'
import { NoteModal } from '../../../components/NoteModal'
import { LinkExternal } from '../../../components/LinkExternal'
import { useOrderDetailContext } from '../OrderDetailContext'
import { Button } from '../../../components/shadcn/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/shadcn/Dropdown'
import { graphql, useFragment } from '../../../gql'
import { useProcessStatus } from '../lib/hooks/useProcessStatus'

export const Footer_OrderFragment = graphql(`
  fragment Footer_OrderFragment on Order {
    id
    processStatus {
      id
      name
    }
    processStatusRelations {
      status {
        id
      }
      created
    }
    carrier {
      id
      name
      requiredShippingAddress
    }
    shippingAddress {
      id
    }
    clearanceItems {
      id
      picked
      packed
      amount
    }
    items {
      id
      type
      packed
    }
    packages {
      sent
      id
      ticketUrl
      carrier {
        id
        name
        color
        position
        tariffs {
          id
          name
        }
      }
    }
    movePacks {
      id
      pdfWOPricesUrl
    }
    reservationUrl
    invoiceUrl
  }
`)

const clientUrl = 'https://app.depoto.cz'

export const Footer: React.FC = () => {
  const context = useOrderDetailContext()
  const currentOrder = useFragment(Footer_OrderFragment, context.order)
  const { core } = useCore()

  const [pdfDocs, setPdfDocs] = useState<any[]>([])
  const refNoteModal = useRef<{ openModal: () => void } | null>(null)
  const refErrorNoteModal = useRef<{ openModal: () => void } | null>(null)
  const {
    currentProcessStatus,
    canUpdateProcessStatus,
    btnNextStatusLabel,
    backSteps,
    isLoading,
    updateProcessStatus,
    statusBeforeError,
    restoreStatusBeforeError,
    cancelReservation,
  } = useProcessStatus(currentOrder)

  const promptErrorNote = () => {
    refErrorNoteModal?.current?.openModal()
  }

  const showOrderDetailInClient = () => {
    console.log(currentOrder?.id)
  }

  useEffect(() => {
    const possiblePdfUrls: any[] = []
    if (currentOrder?.reservationUrl?.length) {
      possiblePdfUrls.push({ id: 'reservationUrl', name: 'objednávka' })
    }
    if (
      currentOrder?.invoiceUrl?.length &&
      (currentProcessStatus === 'packed' || currentProcessStatus === 'dispatched')
    ) {
      possiblePdfUrls.push({ id: 'invoiceUrl', name: 'faktura' })
    }
    if (
      currentOrder?.movePacks?.[0]?.pdfWOPricesUrl?.length &&
      (currentProcessStatus === 'packed' || currentProcessStatus === 'dispatched')
    ) {
      possiblePdfUrls.push({ id: 'movePack', name: 'dodací list' })
    }
    setPdfDocs(possiblePdfUrls)
  }, [currentOrder, currentProcessStatus])

  const printPdf = (urlType: 'invoiceUrl' | 'reservationUrl' | 'movePack') => {
    // console.log(currentOrder?.[urlType])
    if (urlType === 'movePack') {
      downloadFileWithAuth(currentOrder!.movePacks?.[0]?.pdfWOPricesUrl, 'movePack.pdf', core)
      // printDocument(currentOrder!.movePacks?.[0]?.pdfWOPricesUrl, false, false)
    } else if (currentOrder?.[urlType]) {
      printDocument(currentOrder[urlType], false, false, core)
    }
  }

  return (
    <footer className={'p-main bg-white border-t border-b border-cool-grey flex justify-between items-center'}>
      <div className={'flex gap-2 items-center'}>
        {!!currentOrder?.id ? (
          <LinkExternal url={`${clientUrl}/order/${currentOrder.id}`}>
            <Button variant={'linkOutline'} icon={['fas', 'arrow-up-right-from-square']}>
              Otevřít v klientu
            </Button>
          </LinkExternal>
        ) : null}
        <div id="footer-buttons-portal" />
      </div>
      <NoteModal note={''} ref={refNoteModal} onUpdate={note => updateProcessStatus('packing', note)} />
      <NoteModal
        note={''}
        isError={true}
        ref={refErrorNoteModal}
        onUpdate={note => updateProcessStatus('picking_error', note)}
      />
      <div className={'flex gap-2 items-center'}>
        {backSteps.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="h-12">
              <Button variant="outline" icon={['far', 'chevron-down']} iconPosition="right">
                Vrátit stav
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {backSteps.map(s => (
                <DropdownMenuItem key={s} onSelect={() => updateProcessStatus(s as any)}>
                  {translate(s)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button
          onClick={promptErrorNote}
          disabled={isLoading}
          variant="outlineDanger"
          icon={['far', 'triangle-exclamation']}>
          Nahlásit chybu
        </Button>
        {currentOrder.processStatus.id === 'reservation' && (
          <Button onClick={cancelReservation} disabled={isLoading} icon={['far', 'trash-alt']} variant={'destructive'}>
            Zrušit rezervaci
          </Button>
        )}
        {currentProcessStatus === 'picking_error' ? (
          <Button onClick={restoreStatusBeforeError} disabled={isLoading} icon={['far', 'undo']}>
            Vrátit do stavu: {translate(statusBeforeError)}
          </Button>
        ) : (
          <Button
            onClick={() => updateProcessStatus(getNextProcessStatus(currentProcessStatus))}
            disabled={isLoading || !canUpdateProcessStatus}
            icon={['far', 'check']}>
            {btnNextStatusLabel}
          </Button>
        )}
      </div>
    </footer>
  )
}
